import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'

import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { deleteDynamicRule, listDynamicRules } from '../../../api/dynamicRules.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

const WITHOUT_GROUP_CONFIG_FILTER = '__without_group_config__'

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function ApplicationDynamicRulesListPage() {
  const { application, applicationId } = useOutletContext()
  const { confirm, toast } = useFeedback()
  const [filterGroupConfigId, setFilterGroupConfigId] = useState('')
  const [state, setState] = useState({
    status: 'loading',
    items: [],
    groupConfigs: [],
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    setState((prev) => ({
      ...prev,
      status: 'loading',
      items: [],
      error: null,
    }))

    Promise.all([listDynamicRules(applicationId), listGroupConfigs(applicationId)])
      .then(([items, groupConfigs]) => {
        if (cancelled) return
        const sortedItems = [...items].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        setState({
          status: 'ok',
          items: sortedItems,
          groupConfigs: groupConfigs || [],
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          status: 'error',
          items: [],
          groupConfigs: prev.groupConfigs || [],
          error,
        }))
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  const groupConfigsById = useMemo(() => {
    const map = {}
    for (const gc of state.groupConfigs) {
      map[String(gc.id)] = gc
    }
    return map
  }, [state.groupConfigs])

  const hasRulesWithoutGroupConfig = useMemo(
    () => state.items.some((rule) => rule.groupConfigId == null),
    [state.items],
  )

  const filteredItems = useMemo(() => {
    if (!filterGroupConfigId || filterGroupConfigId === '') return state.items
    if (filterGroupConfigId === WITHOUT_GROUP_CONFIG_FILTER) {
      return state.items.filter((rule) => rule.groupConfigId == null)
    }
    return state.items.filter((rule) => String(rule.groupConfigId || '') === String(filterGroupConfigId))
  }, [state.items, filterGroupConfigId])

  async function handleDelete(ruleId) {
    const confirmed = await confirm({
      title: 'Delete dynamic rule',
      message: 'Delete this dynamic rule? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteDynamicRule(applicationId, ruleId)
      setState((prev) => ({
        ...prev,
        items: prev.items.filter((rule) => String(rule.id) !== String(ruleId)),
      }))
    } catch (e) {
      toast({
        type: 'error',
        message: String(e?.body?.detail || e?.message || e),
      })
    }
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">Dynamic rules</div>
        </div>
        <div className="buttonGroup">
          {state.status === 'ok' && (state.groupConfigs.length > 0 || hasRulesWithoutGroupConfig) ? (
            <select
              className="textInput selectFilter"
              value={filterGroupConfigId}
              onChange={(e) => setFilterGroupConfigId(e.target.value)}
              aria-label="Filter by group config"
            >
              <option value="">All group configs</option>
              {hasRulesWithoutGroupConfig ? (
                <option value={WITHOUT_GROUP_CONFIG_FILTER}>Without group config</option>
              ) : null}
              {state.groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          ) : null}
          <Link to={`/applications/${applicationId}/dynamic-rules/new`} className="button buttonPrimary">
            Create
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <p className="muted inlineMessage">
          Loading dynamic rules...
        </p>
      ) : state.status === 'error' ? (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      ) : state.items.length === 0 ? (
        <p className="muted inlineMessage">
          No dynamic rules yet.
        </p>
      ) : filteredItems.length === 0 ? (
        <p className="muted inlineMessage">
          No dynamic rules match the selected filter.
        </p>
      ) : (
        <table className="table" aria-label="Dynamic rules list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Group config</th>
              <th>User param</th>
              <th>Elements</th>
              <th>Created</th>
              <th className="alignRight"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.name || 'Untitled'}</td>
                <td className="muted">{rule.ruleType || 'Value'}</td>
                <td className="muted">
                  {rule.groupConfigId != null
                    ? (groupConfigsById[String(rule.groupConfigId)]?.name || `#${rule.groupConfigId}`)
                    : 'Without group config'}
                </td>
                <td>
                  <span className="mono">{rule.userParam || ''}</span>
                </td>
                <td>{Array.isArray(rule.dynamicRuleElements) ? rule.dynamicRuleElements.length : 0}</td>
                <td>{formatDate(rule.createdAt)}</td>
                <td className="alignRight">
                  <span className="tableActions">
                    <Link
                      to={`/applications/${applicationId}/dynamic-rules/${rule.id}`}
                      className="button buttonGhost"
                    >
                      View
                    </Link>
                    <button
                      type="button"
                      className="button buttonGhost buttonDanger"
                      onClick={() => handleDelete(rule.id)}
                      disabled
                    >
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
